/**
 * Branded install modal for desktop and Android (Path A in the PWA UX plan).
 *
 * The modal only renders when `beforeinstallprompt` has been captured *and*
 * the user passes the value-moment + cooldown gates upstream. We intentionally
 * skip iOS here because Apple disallows programmatic installs.
 */

import { Bell, Download, Wifi, Zap } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { useInstallPrompt, usePromptMemory } from '@/hooks/pwa'

interface InstallPromptProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function InstallPrompt({ open, onOpenChange }: InstallPromptProps) {
  const { canInstall, justInstalled, promptInstall } = useInstallPrompt()
  const memory = usePromptMemory('install', 30)

  // Belt-and-suspenders: even if the parent forgets, never show on devices
  // where the browser hasn't surfaced an install opportunity.
  if (!canInstall && !justInstalled) return null

  const handleInstall = async () => {
    const outcome = await promptInstall()
    onOpenChange(false)
    if (outcome === 'accepted') {
      memory.resolve()
      toast.success('Installing Taqat…', {
        description: 'You can launch it from your dock or home screen in a moment.',
      })
    } else if (outcome === 'dismissed') {
      memory.dismiss()
    }
  }

  const handleNotNow = () => {
    memory.dismiss()
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader className="items-center text-center sm:text-center">
          <div className="mb-2 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary text-primary-foreground">
            <Download className="h-6 w-6" />
          </div>
          <DialogTitle>Make Taqat feel like home</DialogTitle>
          <DialogDescription>
            Install the app for instant access, offline mode, and notifications when your agents finish.
          </DialogDescription>
        </DialogHeader>

        <ul className="space-y-3 py-2">
          <BenefitRow icon={<Zap className="h-4 w-4" />} text="One-click launch — no browser tab" />
          <BenefitRow icon={<Bell className="h-4 w-4" />} text="Get pinged when tasks complete" />
          <BenefitRow icon={<Wifi className="h-4 w-4" />} text="Works offline; syncs when you're back" />
        </ul>

        <DialogFooter className="sm:flex-col sm:gap-2">
          <Button onClick={handleInstall} className="w-full">
            Install Taqat
          </Button>
          <Button variant="ghost" onClick={handleNotNow} className="w-full">
            Not now
          </Button>
        </DialogFooter>
        <p className="text-center text-xs text-muted-foreground">
          You can always install later from Settings.
        </p>
      </DialogContent>
    </Dialog>
  )
}

function BenefitRow({ icon, text }: { icon: React.ReactNode; text: string }) {
  return (
    <li className="flex items-center gap-3 text-sm">
      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-primary">
        {icon}
      </span>
      <span className="text-foreground">{text}</span>
    </li>
  )
}
